import { cn } from '@/lib/utils'
import { DynamicIcon } from 'lucide-react/dynamic'
import type React from 'react'
import Markdown, { defaultUrlTransform } from 'react-markdown'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'
import { ImagemCampanha } from './ImagemCampanha'
import { fatiarMarkdown, idImagem, PROTOCOLO_IMAGEM } from './markdown-tokens'

interface IProps {
    children: string
    campanhaId?: string
    /**
     * Resolve o índice do token em uma URL exibível. Usado na pré-visualização
     * do editor, onde as imagens ainda estão em memória e não têm campanha.
     */
    resolverImagem?: (imagemId: string) => string | undefined
    className?: string
}

/**
 * O token `campanha-imagem:N` não é http(s), então seria descartado tanto pelo
 * schema do sanitizador quanto pelo `urlTransform` do react-markdown. Liberar
 * só o protocolo em `src` mantém o resto do schema padrão intacto — nenhuma
 * tag, atributo ou outro protocolo a mais.
 */
const schemaSanitizacao = {
    ...defaultSchema,
    protocols: {
        ...defaultSchema.protocols,
        src: [...(defaultSchema.protocols?.src ?? []), PROTOCOLO_IMAGEM],
    },
}

function transformarUrl(url: string) {
    if (idImagem(url) !== null) return url

    return defaultUrlTransform(url)
}

function comIcones(node: React.ReactNode): React.ReactNode {
    if (typeof node !== 'string') return node

    return fatiarMarkdown(node).map((pedaco, indice) =>
        pedaco.tipo === 'texto' ? (
            pedaco.valor
        ) : (
            <DynamicIcon
                key={`${pedaco.valor}-${indice}`}
                name={pedaco.valor as never}
                className={cn(
                    'mx-0.5 inline-block align-text-bottom',
                    'size-4',
                )}
                fallback={() => null}
            />
        ),
    )
}

function filhosComIcones(children: React.ReactNode) {
    return Array.isArray(children)
        ? children.map(comIcones)
        : comIcones(children)
}

export function MarkdownView({
    children,
    campanhaId,
    resolverImagem,
    className,
}: IProps) {
    return (
        <div
            className={cn(
                'prose prose-sm dark:prose-invert max-w-none text-foreground',
                'prose-headings:mb-1 prose-headings:mt-2 prose-p:my-1',
                className,
            )}
        >
            <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[[rehypeSanitize, schemaSanitizacao]]}
                urlTransform={transformarUrl}
                components={{
                    p: ({ children }) => <p>{filhosComIcones(children)}</p>,
                    li: ({ children }) => <li>{filhosComIcones(children)}</li>,
                    h1: ({ children }) => (
                        <h1 className="[&_svg]:size-8.5">
                            {filhosComIcones(children)}
                        </h1>
                    ),
                    h2: ({ children }) => (
                        <h2 className="[&_svg]:size-6">
                            {filhosComIcones(children)}
                        </h2>
                    ),
                    h3: ({ children }) => (
                        <h3 className="[&_svg]:size-5.5">
                            {filhosComIcones(children)}
                        </h3>
                    ),
                    h4: ({ children }) => (
                        <h4 className="[&_svg]:size-4">
                            {filhosComIcones(children)}
                        </h4>
                    ),
                    h5: ({ children }) => (
                        <h5 className="[&_svg]:size-3.5">
                            {filhosComIcones(children)}
                        </h5>
                    ),
                    h6: ({ children }) => (
                        <h6 className="[&_svg]:size-3">
                            {filhosComIcones(children)}
                        </h6>
                    ),
                    strong: ({ children }) => (
                        <strong className="[&_svg]:stroke-3">
                            {filhosComIcones(children)}
                        </strong>
                    ),
                    em: ({ children }) => (
                        <em className="[&_svg]:-skew-x-9">
                            {filhosComIcones(children)}
                        </em>
                    ),
                    code: ({ children }) => (
                        <code className="bg-muted rounded p-1">
                            {filhosComIcones(children)}
                        </code>
                    ),
                    pre: ({ children }) => (
                        <pre className="text-sm [&_svg]:size-3.5 bg-muted rounded p-2">
                            {filhosComIcones(children)}
                        </pre>
                    ),
                    blockquote: ({ children }) => (
                        <blockquote className="[&_svg]:-skew-x-9 [&_svg]:stroke-3 border-l-primary border-l-2">
                            {filhosComIcones(children)}
                        </blockquote>
                    ),
                    a: ({ href, children }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {filhosComIcones(children)}
                        </a>
                    ),
                    img: ({ src, alt }) => {
                        const imagemId = idImagem(String(src ?? ''))

                        if (imagemId === null) {
                            return (
                                <img
                                    src={src}
                                    alt={alt ?? ''}
                                    className="rounded-md"
                                />
                            )
                        }

                        const resolvida = resolverImagem?.(imagemId)

                        if (resolvida) {
                            return (
                                <img
                                    src={resolvida}
                                    alt={alt ?? ''}
                                    className="max-w-full rounded-md"
                                />
                            )
                        }

                        // Token sem campanha publicada e sem resolvedor: nada a
                        // buscar, e deixar o token cru viraria imagem quebrada.
                        if (!campanhaId) return null

                        return (
                            <ImagemCampanha
                                campanhaId={campanhaId}
                                imagemId={imagemId}
                                alt={alt}
                            />
                        )
                    },
                }}
            >
                {children}
            </Markdown>
        </div>
    )
}
